<template>
  <div class="absolute top-0 left-0 z-[99999] w-screen h-screen">
    <div class="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-85"></div>
    <div class="fixed inset-0 z-10 w-screen max-h-[80%] max-w-[80%] m-auto">
      <div class="flex flex-col h-full p-1 pt-6 sm:p-10">
        <div
          class="relative flex flex-col w-full h-full p-4 overflow-hidden bg-white rounded-lg shadow-xl">
          <div class="sticky flex justify-between p-3 rounded-lg bg-cyan-light">
            <h1 class="font-medium text-theme-700">{{ modalTitle }}</h1>
            <button @click="emit('cancel')">
              <Icon icon="mdi-close" class="w-4 h-4 ml-2 text-theme-700"></Icon>
            </button>
          </div>
          <div class="flex-1 overflow-y-auto rounded mt-2">
            <div v-if="groupItemNodes.length === 0" class="text-center text-gray-500 py-4">
              {{ $t('messages.noResultsText') }}
            </div>
            <!-- TreeList -->
            <TreeList
              v-model="groupItemNodes"
              hasSearchInput
              hasCheckboxes
              :mode="'view'"></TreeList>
          </div>

          <div class="flex items-center justify-center w-full gap-4 pb-6 mt-auto md:col-span-4">
            <BaseButton color="primary" @click="onSaveFilters">{{
              $t('common.table.applyFilters')
            }}</BaseButton>
            <BaseButton color="primary" outline @click="emit('resetFilters')">{{
              $t('common.table.resetFilters')
            }}</BaseButton>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, computed } from 'vue';
import { Icon } from '@iconify/vue';
import BaseButton from '@/components/common/BaseButton.vue';
import type { TreeType } from '@/types/ComponentTypes';
import { useI18n } from 'vue-i18n';
import { getGroupItemsWithMaterials } from '@/services/api/groups';
import type { FilterGroupItems, GroupItemWithAncestry } from '@/types/Groups';
import TreeList from '@/components/common/TreeList.vue';

const { t: $t } = useI18n();

const props = defineProps<{ activeFilters: FilterGroupItems[] }>();

const emit = defineEmits(['save', 'cancel', 'resetFilters']);

const groupItems = ref<GroupItemWithAncestry[]>([]);
const groupItemNodes = ref<TreeType[]>([]);

onMounted(async () => {
  const response = await getGroupItemsWithMaterials({
    page: 1,
    itemsPerPage: 0,
    query: '',
  });
  groupItems.value = response.data;
  groupItemNodes.value = createTreeListNodes(groupItems.value);

  // Initialize selected state from activeFilters prop
  initializeSelectedFilters();
});

function initializeSelectedFilters() {
  if (props.activeFilters && props.activeFilters.length > 0) {
    const activeIds = props.activeFilters.map((filter) => filter.id);
    markNodesAsSelected(groupItemNodes.value, activeIds);
  }
}

function markNodesAsSelected(nodes: TreeType[], activeIds: string[]) {
  for (const node of nodes) {
    if (activeIds.includes(node.uid)) {
      node.selected = true;
    }
    if (node.children && node.children.length > 0) {
      markNodesAsSelected(node.children, activeIds);
    }
  }
}

function createTreeListNodes(
  items: GroupItemWithAncestry[],
  parentId?: string,
  level: number = 1,
  rootCode: string = ''
): TreeType[] {
  const children = items.filter((item) => item.parentID === parentId);
  return children.map((item, index) => {
    const sequence = index;

    const currentRootCode = rootCode ? `${rootCode}.${sequence}` : `${sequence}`;

    return {
      uid: item.id,
      label: item.name,
      expanded: false,
      parentId: item.parentID ? item.parentID : undefined,
      sequence: sequence,
      level,
      rootCode: currentRootCode,
      children: createTreeListNodes(items, item.id, level + 1, currentRootCode),
    };
  });
}

const modalTitle = computed(() => {
  const selectedCount = selectedNodes.value.length;
  if (selectedCount > 0) {
    return `${$t('common.tooltip.showFilter')} (${selectedCount} selected)`;
  }
  return $t('common.tooltip.showFilter');
});

const selectedNodes = computed(() => {
  return flattenSelectedNodes(groupItemNodes.value);
});

function flattenSelectedNodes(nodes: TreeType[]): TreeType[] {
  const result: TreeType[] = [];

  for (const node of nodes) {
    if (node.selected) {
      result.push(node);
    }

    if (node.children && node.children.length > 0) {
      result.push(...flattenSelectedNodes(node.children));
    }
  }

  return result;
}

function onSaveFilters() {
  const selectedFilters: FilterGroupItems[] = selectedNodes.value.map((node) => {
    const groupItem = groupItems.value.find((item) => item.id === node.uid);
    const parentItem = groupItem?.parentID
      ? groupItems.value.find((item) => item.id === groupItem.parentID)
      : null;

    return {
      id: node.uid,
      name: node.label,
      parentName: parentItem?.name || null,
    };
  });

  emit('save', selectedFilters);
}
</script>
